import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, MenuItem, Paper, Switch, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  createAdminSubscriptionPlan, deleteAdminSubscriptionPlan,
  fetchAdminSubscriptionPlans, updateAdminSubscriptionPlan,
} from "../../../services/adminApi";
import { useToast } from "../../../hook/useToast";
import AdminDashboardLayout from "../AdminDashboardLayout";
import StatsCard from "../StatsCard";
import PlanCard from "../../Subscription/PlanCard";
import { CATEGORY_LABELS, PRICE_TYPE_LABELS, formatRupees } from "../../Subscription/planTheme";

const emptyForm = {
  title: "", category: "counselling", price_type: "fixed", price: "", price_suffix: "",
  tagline: "", features: "", badge: "", is_featured: false, cta_label: "Get Started",
  cta_link: "/council", display_order: "", is_active: true,
};

const errorText = (error, fallback) => {
  const data = error?.response?.data;
  const values = data?.errors || data?.data || data;
  if (typeof data?.message === "string") return data.message;
  if (typeof values === "string") return values;
  if (values && typeof values === "object") return String(Object.values(values).flat()[0] || fallback);
  return fallback;
};

const priceText = (plan) => {
  if (plan.price_type === "free") return "Free";
  if (plan.price_type === "custom") return "Customized";
  return `${formatRupees(plan.price)}${plan.price_suffix ? ` ${plan.price_suffix}` : ""}`;
};

const toForm = (plan) => ({
  title: plan.title || "", category: plan.category || "counselling", price_type: plan.price_type || "fixed",
  price: plan.price_type === "fixed" ? String(Number(plan.price) || "") : "", price_suffix: plan.price_suffix || "",
  tagline: plan.tagline || "", features: (plan.features || []).join("\n"), badge: plan.badge || "",
  is_featured: Boolean(plan.is_featured), cta_label: plan.cta_label || "", cta_link: plan.cta_link || "",
  display_order: String(plan.display_order ?? ""), is_active: plan.is_active !== false,
});

const toPayload = (form) => ({
  ...form,
  title: form.title.trim(),
  price: form.price_type === "fixed" ? form.price || 0 : 0,
  features: form.features.split("\n").map((line) => line.trim()).filter(Boolean),
  display_order: form.display_order === "" ? 0 : Number(form.display_order),
});

const PlanDialog = ({ open, initialValues, previewIndex, submitting, onClose, onSubmit }) => {
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const editMode = Boolean(initialValues);

  useEffect(() => {
    setForm(initialValues ? toForm(initialValues) : emptyForm);
    setError("");
  }, [initialValues, open]);

  const change = (field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) { setError("Title is required."); return; }
    if (form.price_type === "fixed" && !(Number(form.price) > 0)) { setError("Enter a price greater than 0 for a fixed plan."); return; }
    const result = await onSubmit(toPayload(form));
    if (!result?.ok) setError(result?.message || "Unable to save plan.");
  };

  const inputSx = { "& .MuiOutlinedInput-root": { borderRadius: "7px", fontSize: 14 } };
  const preview = toPayload(form);

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ component: "form", onSubmit: submit, sx: { borderRadius: "8px" } }}
    >
      <DialogTitle sx={{ bgcolor: "#292E5C", color: "#fff", fontFamily: "Montserrat", fontSize: 16, fontWeight: 600 }}>
        {editMode ? "Edit Plan" : "Add a Plan"}
      </DialogTitle>
      <DialogContent sx={{ pt: "22px !important", pb: 2.5 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.25fr 1fr" }, gap: 3 }}>
          <Box sx={{ display: "grid", gap: 1.8, alignContent: "start" }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="Title" required autoFocus value={form.title} onChange={change("title")} sx={inputSx} size="small" />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.8 }}>
              <TextField select label="Category" value={form.category} onChange={change("category")} sx={inputSx} size="small">
                {Object.entries(CATEGORY_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </TextField>
              <TextField select label="Price type" value={form.price_type} onChange={change("price_type")} sx={inputSx} size="small">
                {Object.entries(PRICE_TYPE_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </TextField>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.8 }}>
              <TextField
                label="Price (₹)" type="number" value={form.price} onChange={change("price")} sx={inputSx} size="small"
                disabled={form.price_type !== "fixed"} inputProps={{ min: 0, step: "1" }}
                helperText={form.price_type !== "fixed" ? `Shown as "${PRICE_TYPE_LABELS[form.price_type]}"` : " "}
              />
              <TextField label="Price note" placeholder="per session, per year…" value={form.price_suffix} onChange={change("price_suffix")} sx={inputSx} size="small" helperText=" " />
            </Box>
            <TextField label="Tagline" value={form.tagline} onChange={change("tagline")} sx={inputSx} size="small" />
            <TextField
              label="Services (one per line)" multiline minRows={4} value={form.features}
              onChange={change("features")} sx={inputSx}
            />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.8 }}>
              <TextField label="Button text" value={form.cta_label} onChange={change("cta_label")} sx={inputSx} size="small" />
              <TextField
                label="Button link" placeholder="/council or https://…" value={form.cta_link}
                onChange={change("cta_link")} sx={inputSx} size="small"
              />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.8 }}>
              <TextField label="Badge" placeholder="Best Value, Premium…" value={form.badge} onChange={change("badge")} sx={inputSx} size="small" />
              <TextField label="Display order" type="number" value={form.display_order} onChange={change("display_order")} sx={inputSx} size="small" inputProps={{ min: 0 }} />
            </Box>
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              <FormControlLabel control={<Switch checked={form.is_featured} onChange={change("is_featured")} />} label="Highlight this plan" />
              <FormControlLabel control={<Switch checked={form.is_active} onChange={change("is_active")} />} label="Visible to users" />
            </Box>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#667085", mb: 1, textTransform: "uppercase", letterSpacing: 0.6 }}>
              Live preview
            </Typography>
            <Box sx={{ bgcolor: "#F7F8FB", borderRadius: "16px", p: 2 }}>
              <PlanCard plan={preview} index={previewIndex} preview />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={submitting} sx={{ bgcolor: "#FFA54D" }}>
          {submitting ? "Saving..." : editMode ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

PlanDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  initialValues: PropTypes.object,
  previewIndex: PropTypes.number.isRequired,
  submitting: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

const SubscriptionPlansPage = () => {
  const { showToast } = useToast();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, values: null, index: 0 });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const response = await fetchAdminSubscriptionPlans(); setPlans(response?.data?.data || []); }
    catch (error) { showToast(errorText(error, "Failed to load plans"), "error"); } finally { setLoading(false); }
  }, [showToast]);
  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    setSaving(true);
    try {
      if (dialog.values) await updateAdminSubscriptionPlan(dialog.values.plan_id, payload);
      else await createAdminSubscriptionPlan(payload);
      setDialog({ open: false, values: null, index: 0 });
      await load();
      showToast(`Plan ${dialog.values ? "updated" : "added"} successfully`, "success");
      return { ok: true };
    } catch (error) { return { ok: false, message: errorText(error, "Failed to save plan") }; } finally { setSaving(false); }
  };

  const toggleActive = async (plan) => {
    setPlans((current) => current.map((item) => item.plan_id === plan.plan_id ? { ...item, is_active: !plan.is_active } : item));
    try { await updateAdminSubscriptionPlan(plan.plan_id, { is_active: !plan.is_active }); showToast(`${plan.title} is now ${plan.is_active ? "hidden" : "visible"}`, "success"); }
    catch (error) { showToast(errorText(error, "Failed to update plan"), "error"); load(); }
  };

  const remove = async () => {
    try { await deleteAdminSubscriptionPlan(deleteTarget.plan_id); setDeleteTarget(null); await load(); showToast("Plan deleted successfully", "success"); }
    catch (error) { showToast(errorText(error, "Failed to delete plan"), "error"); }
  };

  const activeCount = plans.filter((plan) => plan.is_active).length;

  return <AdminDashboardLayout><Box>
    <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#171725", mb: 1.4 }}>Subscription Plans</Typography>
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2, mb: 2.2 }}>
      <StatsCard value={loading ? "--" : plans.length} label="Total Plans" icon="total" />
      <StatsCard value={loading ? "--" : activeCount} label="Visible to Users" icon="month" bgColor="#FFF9F1" />
      <StatsCard value={loading ? "--" : plans.length - activeCount} label="Hidden Plans" icon="year" bgColor="#F2F6FF" />
    </Box>
    <Paper elevation={0} sx={{ p: 1.25, bgcolor: "rgba(239,239,239,.4)", borderRadius: "7px" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, mb: 1.25, flexWrap: "wrap" }}>
        <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Plans shown on the Subscription page</Typography>
        <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={() => setDialog({ open: true, values: null, index: plans.length })} sx={{ bgcolor: "#FFA54D" }}>Add Plan</Button>
      </Box>
      <TableContainer sx={{ minHeight: 420, bgcolor: "#fff", border: "1px solid #DCDFDE", borderRadius: "7px" }}>
        <Table>
          <TableHead><TableRow sx={{ bgcolor: "#F9FAFB" }}>{["Order", "Plan", "Category", "Price", "Services", "Visible", "Actions"].map((label) => <TableCell key={label}><b>{label}</b></TableCell>)}</TableRow></TableHead>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={28} /></TableCell></TableRow>
              : plans.length ? plans.map((plan, index) => <TableRow key={plan.plan_id} hover sx={{ opacity: plan.is_active ? 1 : 0.55 }}>
                <TableCell>{plan.display_order}</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{plan.title}</Typography>
                    {plan.badge && <Chip size="small" label={plan.badge} sx={{ bgcolor: "#FFF3E0", color: "#C2410C", fontWeight: 600 }} />}
                    {plan.is_featured && <Chip size="small" label="Highlighted" color="warning" variant="outlined" />}
                  </Box>
                  {plan.tagline && <Typography sx={{ fontSize: 12, color: "#667085" }}>{plan.tagline}</Typography>}
                </TableCell>
                <TableCell>{CATEGORY_LABELS[plan.category] || plan.category}</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>{priceText(plan)}</TableCell>
                <TableCell>{(plan.features || []).length}</TableCell>
                <TableCell><Switch checked={plan.is_active} onChange={() => toggleActive(plan)} inputProps={{ "aria-label": `Show ${plan.title}` }} /></TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Tooltip title="Edit"><IconButton aria-label="Edit plan" onClick={() => setDialog({ open: true, values: plan, index })}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton aria-label="Delete plan" color="error" onClick={() => setDeleteTarget(plan)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>)
              : <TableRow><TableCell colSpan={7} align="center" sx={{ py: 7 }}>No plans yet. Add a plan to show it on the Subscription page.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography sx={{ color: "#6E6893", fontSize: 12, mt: 1 }}>Tip: switch a plan off to hide it from users without deleting it.</Typography>
    </Paper>
    <PlanDialog open={dialog.open} initialValues={dialog.values} previewIndex={dialog.index} submitting={saving} onClose={() => setDialog({ open: false, values: null, index: 0 })} onSubmit={save} />
    <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
      <DialogTitle>Delete plan?</DialogTitle>
      <DialogContent><Typography>Remove {deleteTarget?.title || "this plan"} permanently? To hide it temporarily, switch off &quot;Visible&quot; instead.</Typography></DialogContent>
      <DialogActions><Button onClick={() => setDeleteTarget(null)}>Cancel</Button><Button color="error" variant="contained" onClick={remove}>Delete</Button></DialogActions>
    </Dialog>
  </Box></AdminDashboardLayout>;
};

export default SubscriptionPlansPage;
